import { useEffect, useState, useCallback, useRef } from 'react';
import websocketService, { WebSocketMessage, MessageHandler } from '../services/websocketService';

interface UseWebSocketOptions {
  autoConnect?: boolean;
  messageTypes?: string[];
}

interface UseWebSocketReturn {
  isConnected: boolean;
  sendMessage: (message: WebSocketMessage) => void;
  lastMessage: WebSocketMessage | null;
  connect: () => void;
  disconnect: () => void;
}

/**
 * React hook for WebSocket communication
 */
export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const { autoConnect = true, messageTypes = ['*'] } = options;
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const unsubscribesRef = useRef<(() => void)[]>([]);

  useEffect(() => {
    // Subscribe to connection changes
    const unsubConnect = websocketService.onConnectionChange(setIsConnected);
    unsubscribesRef.current.push(unsubConnect);

    // Subscribe to specified message types
    messageTypes.forEach(messageType => {
      const unsubMessage = websocketService.on(messageType, (data) => {
        setLastMessage(data);
      });
      unsubscribesRef.current.push(unsubMessage);
    });

    // Auto-connect if enabled
    if (autoConnect) {
      websocketService.connect();
    }

    // Cleanup on unmount
    return () => {
      unsubscribesRef.current.forEach(unsub => unsub());
      unsubscribesRef.current = [];
    };
  }, []); // Only run on mount

  const sendMessage = useCallback((message: WebSocketMessage) => {
    websocketService.send(message);
  }, []);

  const connect = useCallback(() => {
    websocketService.connect();
  }, []);

  const disconnect = useCallback(() => {
    websocketService.disconnect();
  }, []);

  return {
    isConnected,
    sendMessage,
    lastMessage,
    connect,
    disconnect
  };
}

/**
 * Hook for subscribing to specific WebSocket message types
 */
export function useWebSocketMessage<T = any>(
  messageType: string,
  handler: (data: T) => void
): void {
  useEffect(() => {
    const unsubscribe = websocketService.on(messageType, handler);
    return unsubscribe;
  }, [messageType, handler]);
}

/**
 * Hook for detection-specific WebSocket communication
 */
export function useDetectionWebSocket(model: string = 'dev') {
  const [detectionStatus, setDetectionStatus] = useState<string>('idle');
  const [detectionResult, setDetectionResult] = useState<any>(null);
  
  const { isConnected, sendMessage } = useWebSocket({
    messageTypes: ['detection_status', 'detection_complete']
  });

  useWebSocketMessage('detection_status', (data) => {
    setDetectionStatus(data.status);
  });

  useWebSocketMessage('detection_complete', (data) => {
    setDetectionStatus('complete');
    setDetectionResult(data);
  });

  const startDetection = useCallback(() => {
    console.log('📸 startDetection called - sending WebSocket message with model:', model);
    sendMessage({ type: 'start_detection', model });
    setDetectionStatus('detecting');
  }, [sendMessage, model]);

  return {
    isConnected,
    detectionStatus,
    detectionResult,
    startDetection
  };
}
